'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { ComponentProps, MouseEvent } from 'react'
import { toast } from 'react-toastify'

import { contractQueries } from '@/entities/contract'
import { ACCOUNT_TOAST_CONTAINER_ID } from '@/features/update-account'
import { usePrefetchQuery } from '@/shared/api'
import { useRouter } from '@/shared/i18n'
import { useDialog } from '@/shared/lib'
import { AlertDialog, ConfirmDialog, TextButton } from '@/shared/ui'

import { UpdateDepositReturnAccountDialog } from './dialog'

export const DIALOG = {
  UPDATE_DEPOSIT_RETURN_ACCOUNT: 'UPDATE_DEPOSIT_RETURN_ACCOUNT',
  CONFIRM_CLOSE: 'CONFIRM_CLOSE',
  UPDATE_COMPLETE: 'UPDATE_COMPLETE',
} as const

type UpdateDepositReturnAccountDialog = keyof typeof DIALOG

type Props = ComponentProps<typeof TextButton>

export const UpdateDepositReturnAccountButton = ({
  onClick,
  children,
  ...restProps
}: Props) => {
  const t = useTranslations('deposit_information')

  const router = useRouter()

  const params = useParams()
  const contractId = Number(params?.contractId)

  const { isOpen, open, close, closeAll } = useDialog()
  const queryClient = useQueryClient()

  const { prefetch } = usePrefetchQuery()

  const closeDialog = (key: UpdateDepositReturnAccountDialog) => () => {
    close(key)
  }

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!contractId) {
      return
    }

    const { success } = await prefetch(
      contractQueries.guestDepositReturnAccount({ contractId }),
    )

    if (!success) {
      return
    }

    onClick?.(event)
    open(DIALOG.UPDATE_DEPOSIT_RETURN_ACCOUNT)
  }

  const handleUpdateDialogClose = () => {
    toast.dismiss({ containerId: ACCOUNT_TOAST_CONTAINER_ID })
    open(DIALOG.CONFIRM_CLOSE)
  }

  const handleUpdateSuccess = () => {
    open(DIALOG.UPDATE_COMPLETE, { closeOnOpen: true })
  }

  const handleCloseDialogConfirm = () => {
    closeAll()
  }

  const handleUpdateCompleteDialogClose = async () => {
    close(DIALOG.UPDATE_COMPLETE)

    await queryClient.invalidateQueries({
      queryKey: contractQueries.guestMasterContracts({}).queryKey,
    })
    await queryClient.invalidateQueries({
      queryKey: contractQueries.guestNotifications().queryKey,
    })

    router.refresh()
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  return (
    <>
      <TextButton
        size="small"
        weight="normal"
        onClick={handleClick}
        {...restProps}
      >
        {children}
      </TextButton>

      <UpdateDepositReturnAccountDialog
        isOpen={isOpen(DIALOG.UPDATE_DEPOSIT_RETURN_ACCOUNT)}
        onOpenChange={closeDialog(DIALOG.UPDATE_DEPOSIT_RETURN_ACCOUNT)}
        contractId={contractId}
        onSuccess={handleUpdateSuccess}
        onClose={handleUpdateDialogClose}
      />

      <ConfirmDialog
        isOpen={isOpen(DIALOG.CONFIRM_CLOSE)}
        onOpenChange={closeDialog(DIALOG.CONFIRM_CLOSE)}
        title={t('dialog.exit_confirm.title')}
        content={t('dialog.exit_confirm.description')}
        cancelText={t('dialog.exit_confirm.button.exit')}
        confirmText={t('dialog.exit_confirm.button.continue')}
        onCancel={handleCloseDialogConfirm}
        onConfirm={closeDialog(DIALOG.CONFIRM_CLOSE)}
        preventOutsideClick
      />

      <AlertDialog
        isOpen={isOpen(DIALOG.UPDATE_COMPLETE)}
        onOpenChange={closeDialog(DIALOG.UPDATE_COMPLETE)}
        content={t('dialog.save_complete.description')}
        onConfirm={handleUpdateCompleteDialogClose}
        confirmText={t('dialog.save_complete.button.confirm')}
        preventOutsideClick
      />
    </>
  )
}
